import Draw from "ol/interaction/Draw";
import Feature from "ol/Feature.js";
import { WKT } from "ol/format";
import { GeometryCollection } from "ol/geom";
import { markerSource } from "./layers";
import { maxDrawFeatures, maxDrawPoints, drawDecimals } from "./config";
import { setDrawParams, clearDrawParams } from "./url";
import { setPopupPosition } from "./popup";

export let draw; // Holder for the draw interaction

const drawnFeatures = []; // Drawn features in the order they were drawn, used for undo and the URL
const drawButtonIds = ["draw-point", "draw-line", "draw-polygon"];

// Set up handlers for the draw buttons
export function initDraw(map) {
  document.getElementById("draw-point").addEventListener("click", () => startDrawing(map, "Point"));
  document.getElementById("draw-line").addEventListener("click", () => startDrawing(map, "LineString"));
  document.getElementById("draw-polygon").addEventListener("click", () => startDrawing(map, "Polygon"));
  document.getElementById("draw-undo").addEventListener("click", undoLastDrawing);
  document.getElementById("draw-clear").addEventListener("click", clearDrawing);

  // The source is also cleared by search and reset, keep the drawn features in sync
  markerSource.on("clear", () => {
    drawnFeatures.length = 0;
    updateDrawButtons();
  });

  updateDrawButtons();
}

function startDrawing(map, type) {
  map.removeInteraction(draw);
  if (drawnFeatures.length >= maxDrawFeatures) return;
  addInteraction(map, type);
}

export function addInteraction(map, type) {
  draw = new Draw({
    source: markerSource,
    type: type,
    maxPoints: type === "Point" ? undefined : maxDrawPoints,
  });
  map.addInteraction(draw);

  draw.on("drawstart", function (event) {
    // Drawings and search results are not shown together, remove any search results
    if (drawnFeatures.length === 0) {
      markerSource.clear();
    }
    setPopupPosition(undefined); // Hide the popup if it's open
  });

  draw.on("drawend", function (event) {
    drawnFeatures.push(event.feature);
    map.removeInteraction(draw);
    updateDrawUrl();
    updateDrawButtons();
  });
}

// Add drawn features from geometries, e.g. when restoring from the URL
export function addDrawnFeatures(geometries) {
  const features = geometries.slice(0, maxDrawFeatures).map((geometry) => new Feature(geometry));
  markerSource.addFeatures(features);
  drawnFeatures.push(...features);
  updateDrawButtons();
}

function undoLastDrawing() {
  const feature = drawnFeatures.pop();
  if (!feature) return;
  markerSource.removeFeature(feature);
  setPopupPosition(undefined);
  updateDrawUrl();
  updateDrawButtons();
}

function clearDrawing() {
  markerSource.clear();
  setPopupPosition(undefined);
  clearDrawParams();
}

// Write all drawn features to the URL as one WKT geometry collection
function updateDrawUrl() {
  if (drawnFeatures.length === 0) {
    clearDrawParams();
    return;
  }
  const geometry = new GeometryCollection(drawnFeatures.map((feature) => feature.getGeometry()));
  const format = new WKT();
  setDrawParams(format.writeGeometry(geometry, { decimals: drawDecimals }));
}

function updateDrawButtons() {
  const limitReached = drawnFeatures.length >= maxDrawFeatures;
  drawButtonIds.forEach((id) => (document.getElementById(id).disabled = limitReached));
  document.getElementById("draw-undo").disabled = drawnFeatures.length === 0;
  document.getElementById("draw-clear").disabled = drawnFeatures.length === 0;
}
